import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { programService, ProgramSettings } from "@/services/program.service";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/common/PageHeader";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PlanFeature } from "@/types/permissions";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";

interface CreateProgramProps {
  onSuccess?: (programId?: string) => void;
  onCancel?: () => void;
}

export default function CreateProgram({ onSuccess, onCancel }: CreateProgramProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasFeature } = usePermissions();
  
  // Check if there are existing programs
  const { data: existingPrograms } = useQuery({
    queryKey: ["my-programs"],
    queryFn: () => programService.getMyPrograms(),
  });

  const hasExistingPrograms = existingPrograms && (existingPrograms as any[]).length > 0;
  const hasSneakylinkFeature = hasFeature(PlanFeature.REVENUE_LOCK_SNEAKYLINK);
  const hasProgramNamingFeature = hasFeature(PlanFeature.PROGRAM_NAMING);

  const [settings, setSettings] = useState<
    ProgramSettings & {
      commissionValueInput?: string;
      discountValueInput?: string;
    }
  >({
    commissionType: "PERCENTAGE",
    commissionValue: 0,
    discountValue: 0,
    minimumPayoutAmount: 0,
    automaticApproval: false,
    attributionMethod: "STATIC_CODE",
    commissionValueInput: "",
    discountValueInput: "",
    name: "",
  });

  const [errors, setErrors] = useState<{
    commissionValue?: string;
    discountValue?: string;
  }>({});

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      // Use createProgram if no programs exist, otherwise use createAdditionalProgram
      if (!hasExistingPrograms) {
        return programService.createProgram(settings);
      } else {
        return programService.createAdditionalProgram(settings);
      }
    },
    onSuccess: async (program) => {
      // Invalidate queries to ensure fresh data when navigating to edit page
      await queryClient.invalidateQueries({ queryKey: ["my-programs"] });
      await queryClient.invalidateQueries({ queryKey: ["program"] });
      await queryClient.invalidateQueries({ queryKey: ["program", program.id] });
      
      if (onSuccess) {
        onSuccess(program.id);
      } else {
        navigate(`/brand/programs/${program.id}`);
      }
    },
  });

  const validate = (): boolean => {
    const newErrors: { commissionValue?: string; discountValue?: string } = {};

    // Validate commission value
    if (!settings.commissionValueInput || settings.commissionValueInput.trim() === "") {
      newErrors.commissionValue = "Commission rate is required";
    } else if (settings.commissionValue <= 0) {
      newErrors.commissionValue = "Commission rate must be greater than 0";
    } else if (settings.commissionValue > 100) {
      newErrors.commissionValue = "Commission percentage cannot exceed 100%";
    }

    // Validate discount value
    if (!settings.discountValueInput || settings.discountValueInput.trim() === "") {
      newErrors.discountValue = "Discount value is required";
    } else if (settings.discountValue < 0) {
      newErrors.discountValue = "Discount value cannot be negative";
    } else if (settings.discountValue > 100) {
      newErrors.discountValue = "Discount percentage cannot exceed 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure sneakylink is not selected if user doesn't have the feature
    if (!hasSneakylinkFeature && settings.attributionMethod === "SNEAKYLINK") {
      toast.error("Sneakylink is not available in your current plan. Please upgrade to use this feature.");
      return;
    }
    if (validate()) {
      mutate();
    }
  };

  return (
    <div className="space-y-6 h-full w-full p-4">
      <PageHeader
        title="Create program"
        description="Configure your affiliate program settings below"
      />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-8">
        {/* Commission Rate */}
        <div
          id="PSF-commission-rate-field"
          className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8"
        >
          <div>
            <h3 className="text-sm font-medium text-foreground">
              Commission rate
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Default commission rate for affiliates
            </p>
          </div>
        <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none"></div>
          <input
            type="number"
                step="1"
                value={settings.commissionValueInput ?? ""}
                onChange={(e) => {
                  const input = e.target.value;
                  setSettings({
                    ...settings,
                    commissionValueInput: input,
                    commissionValue: input === "" ? 0 : Number(input),
                  });
                  // Clear error when user starts typing
                  if (errors.commissionValue) {
                    setErrors({ ...errors, commissionValue: undefined });
                  }
                }}
                onBlur={() => {
                  // Validate on blur
                  if (!settings.commissionValueInput || settings.commissionValueInput.trim() === "") {
                    setErrors({ ...errors, commissionValue: "Commission rate is required" });
                  } else if (settings.commissionValue <= 0) {
                    setErrors({ ...errors, commissionValue: "Commission rate must be greater than 0" });
                  } else if (settings.commissionValue > 100) {
                    setErrors({ ...errors, commissionValue: "Commission percentage cannot exceed 100%" });
                  }
                }}
                min="0"
                max="100"
                className={`w-full pl-2 pr-8 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.commissionValue
                    ? "border-red-300 dark:border-red-800 focus:ring-red-500"
                    : "border-border focus:ring-brand-500"
                }`}
              />
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground">%</span>
              </div>
            </div>
            {errors.commissionValue && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.commissionValue}</p>
            )}
          </div>
        </div>

        {/* Discount Percentage */}
        <div
          id="PSF-discount-percentage-field"
          className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8"
        >
          <div>
            <h3 className="text-sm font-medium text-foreground">
              Discount percentage
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Discount offered to customers
            </p>
        </div>
        <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none"></div>
          <input
            type="number"
                value={settings.discountValueInput ?? ""}
                onChange={(e) => {
                  const input = e.target.value;
                  setSettings({
                    ...settings,
                    discountValueInput: input,
                    discountValue: input === "" ? 0 : Number(input),
                  });
                  // Clear error when user starts typing
                  if (errors.discountValue) {
                    setErrors({ ...errors, discountValue: undefined });
                  }
                }}
                onBlur={() => {
                  // Validate on blur
                  if (!settings.discountValueInput || settings.discountValueInput.trim() === "") {
                    setErrors({ ...errors, discountValue: "Discount percentage is required" });
                  } else if (settings.discountValue < 0) {
                    setErrors({ ...errors, discountValue: "Discount percentage cannot be negative" });
                  }
                }}
                min="0"
                max="100"
                className={`w-full pl-2 pr-8 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.discountValue
                    ? "border-red-300 dark:border-red-800 focus:ring-red-500"
                    : "border-border focus:ring-brand-500"
                }`}
              />
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground">%</span>
              </div>
            </div>
            {errors.discountValue && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.discountValue}</p>
            )}
          </div>
        </div>

        {/* Program Name - Only for Growth and Scale plans */}
        {hasProgramNamingFeature && (
          <div
            id="PSF-program-name-field"
            className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8"
          >
            <div>
              <h3 className="text-sm font-medium text-foreground">
                Program name
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Give your affiliate program a custom name for better visibility
              </p>
            </div>
            <div>
              <input
                type="text"
                value={settings.name ?? ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    name: e.target.value,
                  })
                }
                placeholder="e.g., Summer Campaign, VIP Program"
                maxLength={100}
                className="w-full pl-2 pr-2 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Optional: Leave empty to use a generic program name
              </p>
            </div>
          </div>
        )}

        {/* Attribution Method */}
        <div
          id="PSF-attribution-method-field"
          className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8"
        >
        <div>
            <h3 className="text-sm font-medium text-foreground">
              Attribution method
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose how affiliates will track their referrals
            </p>
        </div>
        <div>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="attributionMethod"
                  value="STATIC_CODE"
                  checked={settings.attributionMethod === "STATIC_CODE"}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      attributionMethod: e.target.value as "STATIC_CODE" | "SNEAKYLINK",
                    })
                  }
                  className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm font-medium text-muted-foreground">Static Code</span>
              </label>
              <label 
                className={`flex items-center space-x-2 ${
                  hasSneakylinkFeature ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                }`}
                title={!hasSneakylinkFeature ? 'Sneakylink uses single-usage codes to prevent self-referrals and affiliate fraud.' : ''}
              >
                <input
                  type="radio"
                  name="attributionMethod"
                  value="SNEAKYLINK"
                  checked={settings.attributionMethod === "SNEAKYLINK"}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      attributionMethod: e.target.value as "STATIC_CODE" | "SNEAKYLINK",
                    })
                  }
                  disabled={!hasSneakylinkFeature}
                  className="w-4 h-4 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm font-medium text-muted-foreground">
                  Sneakylink
                  {!hasSneakylinkFeature && (
                    <span className="ml-1 text-xs text-muted-foreground">(Upgrade required)</span>
                  )}
                </span>
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {settings.attributionMethod === "SNEAKYLINK"
                ? "Sneakylinks generate unique tracking links for each affiliate"
                : "Static codes require affiliates to use a fixed discount code"}
            </p>
            {!hasSneakylinkFeature && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                ⚠️ Sneakylink requires a Scale plan. <a href="/brand/pricing" className="underline hover:text-amber-700">Upgrade now</a>
              </p>
            )}
          </div>
        </div>

        {/* Automatic Approval Toggle - available on every program, default and
            custom alike. Custom programs are joined through their share link,
            so auto-approval is just as meaningful there. */}
        <div
          id="PSF-automatic-approval-field"
          className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8"
        >
          <div>
            <h3 className="text-sm font-medium text-foreground">
              Automatic approval
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {hasExistingPrograms
                ? "Automatically approve affiliates who join through this program's share link"
                : "Automatically approve new affiliate applications"}
            </p>
          </div>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() =>
                setSettings({
                  ...settings,
                  automaticApproval: !settings.automaticApproval,
                })
              }
              className={`${
                settings.automaticApproval
                  ? "bg-brand-600"
                  : "bg-muted"
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  settings.automaticApproval
                    ? "translate-x-5"
                    : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 mt-6">
          <div></div>
          <div className="flex justify-start space-x-4">
            <button
              type="button"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else {
                  navigate(-1);
                }
              }}
              className="px-4 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-muted-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              Cancel
            </button>
            <Button
              id="PSF-save-program-button"
              type="submit"
              disabled={isPending || !settings.commissionValueInput || settings.commissionValue <= 0 || !settings.discountValueInput || settings.discountValue < 0}
            >
              {isPending && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>Create program</span>
            </Button>
        </div>
        </div>
      </form>
    </div>
  );
}


